import {
	BG,
	CZ,
	DE,
	DK,
	EE,
	ES,
	EU,
	FI,
	FR,
	GB,
	GR,
	HR,
	HU,
	IE,
	IT,
	LT,
	LV,
	MT,
	NL,
	NO,
	PL,
	PT,
	RO,
	SE,
	SI,
	SK,
	TR,
	US,
} from "country-flag-icons/react/3x2";
import { getLocaleConfig, type Locale } from "@/config/locale";
import { channelSlugToFlagIso } from "@/lib/flag-emoji";
import { cn } from "@/lib/utils";

/**
 * The exhaustive per-locale flag table. `Record<Locale, typeof DE>` is the compile-time forcing
 * function: a new registry locale without an entry here is a type error, and the registry contract
 * is asserted key-for-key in `src/config/locale.test.ts`.
 */
const localeFlags: Record<Locale, typeof DE> = {
	// locale-literals-ok: AGENTS.md — exhaustive Record<Locale, typeof DE>; the type system is the gate, so no derivation is needed or wanted.
	de: DE,
	en: GB,
	es: ES,
	tr: TR,
	fr: FR,
	it: IT,
	nl: NL,
	pl: PL,
	pt: PT,
	sv: SE,
	da: DK,
	cs: CZ,
	nb: NO,
	et: EE,
	fi: FI,
	ga: IE,
	el: GR,
	hr: HR,
	lv: LV,
	mt: MT,
	ro: RO,
	sk: SK,
	sl: SI,
	hu: HU,
	// locale-literals-ok: AGENTS.md — bg/lt rows of the exhaustive Record<Locale, …> above; the sweep caps one multi-line map at 24 entries, so the type system stays the gate.
	bg: BG,
	lt: LT,
};

const channelFlags = {
	EU,
	US,
} as const;

/**
 * SVG flag next to footer language select (matches current locale).
 */
export function FooterLocaleFlag({
	locale,
	className,
	compact,
}: {
	locale: Locale;
	className?: string;
	compact?: boolean;
}) {
	const Flag = localeFlags[locale];
	const label = getLocaleConfig(locale).nativeName;
	return (
		<span
			className={cn(
				"inline-flex shrink-0 overflow-hidden rounded-[3px] shadow-sm ring-1 ring-border",
				className,
			)}
			title={label}
			aria-hidden
		>
			<Flag className={compact ? "block h-3.5 w-[21px]" : "block h-full w-[30px]"} />
		</span>
	);
}

/**
 * SVG flag for EU / US channel (Saleor storefront region).
 */
export function FooterChannelFlag({ channelSlug, className }: { channelSlug: string; className?: string }) {
	const iso = channelSlugToFlagIso(channelSlug);
	const Flag = channelFlags[iso];
	const label = iso === "US" ? "US" : "EU";
	return (
		<span
			className={cn(
				"inline-flex shrink-0 overflow-hidden rounded-[3px] shadow-sm ring-1 ring-border",
				className,
			)}
			title={label}
			aria-hidden
		>
			<Flag className="block h-5 w-[30px]" />
		</span>
	);
}
